/**
 * Permission utility functions and constants
 */

// Common permission tokens
export const PERMISSIONS = {
  // User management
  ALLOW_USER_ADMIN: 'allow_user_admin',
  ALLOW_USER_LIST: 'allow_user_list',
  ALLOW_USER_EDIT: 'allow_user_edit',
  ALLOW_USER_CREATE: 'allow_user_create',
  ALLOW_USER_DELETE: 'allow_user_delete',

  // Project management
  ALLOW_PROJECT_MANAGEMENT: 'allow_project_management',
  ALLOW_PROJECT_CREATE: 'allow_project_create',
  ALLOW_PROJECT_EDIT: 'allow_project_edit',
  ALLOW_PROJECT_DELETE: 'allow_project_delete',

  // Financial
  ALLOW_FINANCIAL_VIEW: 'allow_financial_view',
  ALLOW_INVOICE_MANAGEMENT: 'allow_invoice_management',

  // Reports & Export
  ALLOW_REPORTS_VIEW: 'allow_reports_view',
  ALLOW_REPORTS_EXPORT: 'allow_reports_export',
  ALLOW_DATA_EXPORT: 'allow_data_export',
  ALLOW_EXPORT: 'allow_export',

  // Settings
  ALLOW_SYSTEM_SETTINGS: 'allow_system_settings',
  ALLOW_SETTINGS_EDIT: 'allow_settings_edit',
  ALLOW_TEAM_SETTINGS: 'allow_team_settings',

  // Add more as needed...
};

/**
 * Helper function to check permissions from Redux state
 */
export const checkPermission = (state, permissionToken) => {
  const userPermissions = (state.user && state.user.permissions) || [];
  return userPermissions.includes(permissionToken);
};

/**
 * Helper function to check multiple permissions (any)
 */
export const checkAnyPermission = (state, permissionTokens) => {
  const userPermissions = (state.user && state.user.permissions) || [];
  return permissionTokens.some(token => userPermissions.includes(token));
};

/**
 * Helper function to check multiple permissions (all)
 */
export const checkAllPermissions = (state, permissionTokens) => {
  const userPermissions = (state.user && state.user.permissions) || [];
  return permissionTokens.every(token => userPermissions.includes(token));
};

/**
 * Permit function: checks if a permission is allowed (not denied)
 * @param {Array} userPermissions - Array of user permissions
 * @param {string} token - Permission token to check (without 'deny_' prefix)
 * @returns {boolean} - True if not denied, false if denied
 */
export const permit = (userPermissions, token) => {
  const denyToken = `allow_${token}`;
  //console.log(denyToken);
  //console.log(userPermissions);
  return userPermissions.includes(denyToken);
};

/**
 * Create a permit function that has access to userPermissions
 * This is meant to be used in components with Redux connection
 * @param {Array} userPermissions - Array of user permissions from Redux
 * @returns {Function} - Permit function that only needs the token
 */
export const createPermitFunction = (userPermissions) => {
  //console.log(userPermissions);
  return (token) => permit(userPermissions, token);
};

/**
 * Get user's access level based on permissions
 */
export const getUserAccessLevel = (permissions) => {
  if (permissions.includes(PERMISSIONS.ALLOW_USER_ADMIN) ||
      permissions.includes(PERMISSIONS.ALLOW_SYSTEM_SETTINGS)) {
    return 'admin';
  }
  if (permissions.includes(PERMISSIONS.ALLOW_PROJECT_MANAGEMENT) ||
      permissions.includes(PERMISSIONS.ALLOW_USER_EDIT)) {
    return 'manager';
  }
  if (permissions.includes(PERMISSIONS.ALLOW_PROJECT_CREATE) ||
      permissions.includes(PERMISSIONS.ALLOW_EXPORT)) {
    return 'advanced';
  }
  return 'basic';
};

export default {
  PERMISSIONS,
  checkPermission,
  checkAnyPermission,
  checkAllPermissions,
  permit,
  createPermitFunction,
  getUserAccessLevel
};
